import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ethers } from 'ethers'
import { NFT_CONFIG } from '@/config/nft'

export interface WalletState {
  address: string | null
  isConnected: boolean
  provider: ethers.BrowserProvider | null
  signer: ethers.JsonRpcSigner | null
  chainId: number | null
  balance: string
}

export const useWalletStore = defineStore('wallet', () => {
  // State
  const address = ref<string | null>(null)
  const isConnected = ref(false)
  const provider = ref<ethers.BrowserProvider | null>(null)
  const signer = ref<ethers.JsonRpcSigner | null>(null)
  const chainId = ref<number | null>(null)
  const balance = ref('0')
  const isConnecting = ref(false)

  // X Layer Mainnet configuration
  const X_LAYER_CHAIN_ID = 196
  const X_LAYER_RPC_URL = 'https://rpc.xlayer.tech'
  const X_LAYER_CHAIN_CONFIG = {
    chainId: '0xC4', // 196 in hex
    chainName: 'X Layer Mainnet',
    nativeCurrency: {
      name: 'OKB',
      symbol: 'OKB',
      decimals: 18
    },
    rpcUrls: [X_LAYER_RPC_URL],
    blockExplorerUrls: ['https://www.oklink.com/xlayer']
  }

  // Getters
  const isOnXLayer = computed(() => chainId.value === X_LAYER_CHAIN_ID)
  const shortAddress = computed(() => {
    if (!address.value) return ''
    return `${address.value.slice(0, 6)}...${address.value.slice(-4)}`
  })

  // Check if wallet was previously connected on page load
  const checkConnection = async () => {
    if (!window.ethereum) return

    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' })
      if (accounts.length > 0) {
        // Auto-connect if previously connected
        const web3Provider = new ethers.BrowserProvider(window.ethereum)
        const web3Signer = await web3Provider.getSigner()
        const network = await web3Provider.getNetwork()

        provider.value = web3Provider
        signer.value = web3Signer
        address.value = accounts[0]
        chainId.value = Number(network.chainId)
        isConnected.value = true

        await updateBalance()

        // Listen for account changes
        window.ethereum.on('accountsChanged', handleAccountsChanged)
        window.ethereum.on('chainChanged', handleChainChanged)
      }
    } catch (error) {
      console.error('Failed to check connection:', error)
    }
  }

  // Actions
  const connectWallet = async () => {
    if (isConnecting.value) return
    
    try {
      isConnecting.value = true
      
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed')
      }

      // Request account access
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      })

      if (accounts.length === 0) {
        throw new Error('No accounts found')
      }

      // Create provider and signer
      const web3Provider = new ethers.BrowserProvider(window.ethereum)
      const web3Signer = await web3Provider.getSigner()
      const network = await web3Provider.getNetwork()

      // Update state
      provider.value = web3Provider
      signer.value = web3Signer
      address.value = accounts[0]
      chainId.value = Number(network.chainId)
      isConnected.value = true

      // Get balance
      await updateBalance()

      // Switch to X Layer if not already on it
      if (chainId.value !== X_LAYER_CHAIN_ID) {
        await switchToXLayer()
      }

      // Listen for account changes
      window.ethereum.on('accountsChanged', handleAccountsChanged)
      window.ethereum.on('chainChanged', handleChainChanged)

    } catch (error: any) {
      console.error('Failed to connect wallet:', error)
      throw error
    } finally {
      isConnecting.value = false
    }
  }

  const disconnectWallet = () => {
    address.value = null
    isConnected.value = false
    provider.value = null
    signer.value = null
    chainId.value = null
    balance.value = '0'

    // Remove event listeners
    if (window.ethereum) {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged)
      window.ethereum.removeListener('chainChanged', handleChainChanged)
    }
  }

  const switchToXLayer = async () => {
    if (!window.ethereum) return

    try {
      // Try to switch to X Layer
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: X_LAYER_CHAIN_CONFIG.chainId }]
      })
    } catch (switchError: any) {
      // If the chain is not added, add it
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [X_LAYER_CHAIN_CONFIG]
          })
        } catch (addError) {
          console.error('Failed to add X Layer network:', addError)
          throw addError
        }
      } else {
        console.error('Failed to switch to X Layer:', switchError)
        throw switchError
      }
    }
  }

  const updateBalance = async () => {
    if (!provider.value || !address.value) return

    try {
      const balanceWei = await provider.value.getBalance(address.value)
      balance.value = ethers.formatEther(balanceWei)
    } catch (error) {
      console.error('Failed to get balance:', error)
    }
  }

  const handleAccountsChanged = (accounts: string[]) => {
    if (accounts.length === 0) {
      disconnectWallet()
    } else {
      address.value = accounts[0]
      updateBalance()
    }
  }

  const handleChainChanged = (newChainId: string) => {
    chainId.value = parseInt(newChainId, 16)
    updateBalance()
  }

  // Mock NFT contract interaction (replace with actual contract when ready)
  const mintNFT = async (quantity: number = 1) => {
    if (!signer.value || !isOnXLayer.value) {
      throw new Error('Please connect wallet and switch to X Layer')
    }

    try {
      // Mock transaction - replace with actual contract call
      console.log(`Minting ${quantity} NFT(s)...`)
      
      // Simulate transaction
      const tx = await signer.value.sendTransaction({
        to: NFT_CONFIG.CONTRACT_ADDRESS, // Replace with actual contract address
        value: ethers.parseEther(NFT_CONFIG.calculateTotalCost(quantity).toString()),
        data: '0x' // Replace with actual mint function call data
      })

      console.log('Transaction sent:', tx.hash)
      
      // Wait for confirmation
      const receipt = await tx.wait()
      console.log('Transaction confirmed:', receipt)
      
      // Update balance after transaction
      await updateBalance()
      
      return receipt
    } catch (error) {
      console.error('Minting failed:', error)
      throw error
    }
  }

  return {
    // State
    address,
    isConnected,
    provider,
    signer,
    chainId,
    balance,
    isConnecting,
    
    // Getters
    isOnXLayer,
    shortAddress,
    
    // Actions
    connectWallet,
    disconnectWallet,
    switchToXLayer,
    updateBalance,
    mintNFT,
    checkConnection
  }
})

// Extend Window interface for TypeScript
declare global {
  interface Window {
    ethereum?: any
  }
}